/**
 * legendStylesResolver.js
 *
 * Хелпер для получения items легенд из layerStyles
 * Позволяет избежать дублирования данных между layerStyles и legends конфигами
 *
 * Использование:
 * const items = getStyleLegendItems(HOUSES_STYLE, 'yearGradient');
 * const items = getStyleLegendItems(HOUSES_STYLE, 'populationScale');
 */

/**
 * Получает items из стиля слоя по ключу
 * Поддерживает оба формата:
 * 1. Новый (рекомендуется): style[key] = [] (массив напрямую)
 * 2. Старый (совместимость): style[key] = { filterItems: [], legendItems: [] }
 *
 * @param {object} style - Объект стиля слоя (например, HOUSES_STYLE)
 * @param {string} styleKey - Ключ в стиле (например, 'yearGradient', 'populationScale')
 * @returns {array} Массив items для легенды
 *
 * @example
 * const items = getStyleLegendItems(HOUSES_STYLE, 'yearGradient');
 * // Вернёт: HOUSES_STYLE.yearGradient (если массив) или HOUSES_STYLE.yearGradient.legendItems (если объект)
 */
export const getStyleLegendItems = (style, styleKey) => {
    if (!style || !styleKey) {
        return [];
    }

    const styleSection = style[styleKey];
    if (!styleSection) {
        console.warn(`Style section "${styleKey}" not found in provided style`);
        return [];
    }

    // ✅ Новый формат: массив напрямую
    if (Array.isArray(styleSection)) {
        return styleSection;
    }

    // ✅ Формат с items (HOUSES_STYLE.yearGradient.items, HOUSES_STYLE.populationScale.items)
    if (styleSection.items && Array.isArray(styleSection.items)) {
        return styleSection.items;
    }

    // ✅ Старый формат: объект с legendItems или filterItems
    if (styleSection.legendItems) {
        return styleSection.legendItems;
    }

    if (styleSection.filterItems) {
        return styleSection.filterItems;
    }

    console.warn(`No items found in style["${styleKey}"] (expected array, legendItems, or filterItems)`);
    return [];
};

/**
 * Получает legendItems и merge с дополнительными полями если нужны
 * Полезно если легенде нужны поля из нескольких мест
 *
 * @param {object} style - Объект стиля слоя
 * @param {string} styleKey - Ключ в стиле
 * @param {function} transformer - Опциональная функция трансформации items
 * @returns {array} Трансформированный массив items
 *
 * @example
 * const items = getStyleLegendItemsTransformed(
 *   HOUSES_STYLE,
 *   'yearGradient',
 *   items => items.map(item => ({ ...item, type: 'gradient' }))
 * );
 */
export const getStyleLegendItemsTransformed = (style, styleKey, transformer) => {
    const items = getStyleLegendItems(style, styleKey);

    if (transformer && typeof transformer === 'function') {
        return transformer(items);
    }

    return items;
};
